import { useRef, useCallback, useEffect } from 'react';
import { ACHIEVEMENTS } from '../data/achievements';

const CONTINENT_MAP = {
    'US': 'NA', 'CANADA': 'NA', 'MEXICO': 'NA',
    'UK': 'EU', 'FRANCE': 'EU', 'GERMANY': 'EU', 'ITALY': 'EU', 'SPAIN': 'EU', 'NORWAY': 'EU', 'RUSSIA': 'EU',
    'JAPAN': 'AS', 'CHINA': 'AS', 'INDIA': 'AS', 'SOUTH KOREA': 'AS', 'INDONESIA': 'AS',
    'AUSTRALIA': 'OC', 'NEW ZEALAND': 'OC',
    'BRAZIL': 'SA', 'ARGENTINA': 'SA', 'PERU': 'SA',
    'SOUTH AFRICA': 'AF', 'EGYPT': 'AF',
    'ANTARCTICA': 'AN'
};

export const useAchievements = (userSettings, setUserSettings, onUnlock, onStreakUpdate, onUnlockAchievement) => {
    const sessionStart = useRef(Date.now());
    const straightKeyStart = useRef(null);
    const straightKeyChars = useRef(0);
    const perfectStreak = useRef(0);
    const bugChars = useRef(0);
    const micChars = useRef(0);
    const pileupCount = useRef(0);
    const contestStreak = useRef(0);

    // New Refs for tracking
    const socialMessagesSent = useRef(0);
    const socialMessagesDecoded = useRef(0);
    const wxDrills = useRef(0);
    const newsDrills = useRef(0);
    const storyDrills = useRef(0);
    const wasLowHealth = useRef(false);
    const qCodes = useRef(0);
    const abbrs = useRef(0);

    const unlock = useCallback((id) => {
        if (!userSettings) return;
        const unlocked = userSettings.achievements || [];
        if (unlocked.includes(id)) return;

        const achievement = ACHIEVEMENTS.find(a => a.id === id);
        if (achievement) {
            setUserSettings(prev => ({
                ...prev,
                achievements: [...(prev.achievements || []), id]
            }));
            if (onUnlock) onUnlock(achievement);
            if (onUnlockAchievement) onUnlockAchievement(id);
        }
    }, [userSettings, setUserSettings, onUnlock]);

    const checkTimeAchievements = useCallback(() => {
        const now = new Date();
        const hour = now.getHours();
        if (hour >= 2 && hour < 4) unlock('night_owl');
        if (hour >= 5 && hour < 7) unlock('early_bird');
        // Gray line approximation (6am/6pm +/- 1 hour)
        if ((hour >= 5 && hour <= 7) || (hour >= 17 && hour <= 19)) unlock('gray_line');

        // Morse Code Day (April 27)
        if (now.getMonth() === 3 && now.getDate() === 27) unlock('morse_code_day');
    }, [unlock]);

    // Check for retroactive achievements (e.g. if gallery is already full but achievement not awarded)
    useEffect(() => {
        if (!userSettings) return;

        // 1. Constellation Cartographer: Complete all 15 constellations
        const gallery = userSettings.constellationHunterGallery || [];
        if (gallery.length >= 15) {
            unlock('constellation_cartographer');
        }

        // 2. Master Navigator: Unlock all 10 modes
        if (userSettings.constellationHunterModesUnlocked) {
            unlock('master_navigator');
        }
    }, [userSettings?.constellationHunterGallery?.length, userSettings?.constellationHunterModesUnlocked, unlock]);

    const checkSessionAchievements = useCallback((inputMode) => {
        const durationMinutes = (Date.now() - sessionStart.current) / 60000;
        if (durationMinutes >= 60) unlock('marathon');

        if (inputMode === 'straight_key') {
            if (!straightKeyStart.current) straightKeyStart.current = Date.now();
            const skDuration = (Date.now() - straightKeyStart.current) / 60000;
            if (skDuration >= 15) unlock('iron_arm');
        } else {
            straightKeyStart.current = null;
        }
    }, [unlock]);

    const checkProgress = useCallback((type, data) => {
        switch (type) {
            case 'wpm':
                if (data.wpm >= 30) unlock('speed_demon_30');
                if (data.wpm >= 40) unlock('speed_demon_40');
                break;
            case 'qso_complete':
                unlock('first_qso');
                break;
            case 'accuracy':
                if (data.isCorrect) {
                    perfectStreak.current += 1;
                    if (perfectStreak.current >= 10) unlock('perfect_copy');
                    // Send streak update to challenge system
                    if (onStreakUpdate) {
                        onStreakUpdate(perfectStreak.current);
                    }
                } else {
                    perfectStreak.current = 0;
                    // Also send 0 streak on error
                    if (onStreakUpdate) {
                        onStreakUpdate(0);
                    }
                }
                break;
            case 'char_sent':
                if (data.inputMode === 'straight_key') {
                    straightKeyChars.current += 1;
                    if (straightKeyChars.current >= 500) unlock('straight_key_sk');
                } else if (data.inputMode === 'bug') {
                    bugChars.current += 1;
                    if (bugChars.current >= 100) unlock('the_bug');
                } else if (data.inputMode === 'mic') {
                    micChars.current += 1;
                    if (micChars.current >= 50) unlock('voice_of_morse');
                }
                break;
            case 'qsb':
                if (data.qsbLevel >= 0.8 && data.isExchangeComplete) unlock('qrp_hero');
                break;
            case 'sos':
                unlock('sos');
                break;
            case 'level_complete':
                if (data.accuracy === 100 && (!data.farnsworthWpm || data.farnsworthWpm === data.wpm)) {
                    unlock('the_natural');
                }
                // Koch Progression Unlocks
                if (data.level >= 5) unlock('koch_5');
                if (data.level >= 10) unlock('koch_10');
                if (data.level >= 20) unlock('koch_20');
                if (data.level >= 40) unlock('koch_40');

                // Check if user completed all Koch characters (level 40) with specific input modes
                if (data.level >= 40) {
                    if (data.inputMode === 'paddle') {
                        unlock('paddle_master');
                    } else if (data.inputMode === 'bug') {
                        unlock('bug_virtuoso');
                    } else if (data.inputMode === 'straight_key' || data.inputMode === 'straight') {
                        unlock('straight_key_master');
                    }
                }
                break;
            case 'country':
                const country = data.country;
                if (!country) break;

                const stats = userSettings.stats || {};
                const visitedCountries = stats.visitedCountries || [];
                const visitedContinents = stats.visitedContinents || [];

                let updated = false;
                let newCountries = [...visitedCountries];
                let newContinents = [...visitedContinents];

                if (!newCountries.includes(country)) {
                    newCountries.push(country);
                    updated = true;
                }

                const continent = CONTINENT_MAP[country];
                if (continent && !newContinents.includes(continent)) {
                    newContinents.push(continent);
                    updated = true;
                }

                if (updated) {
                    setUserSettings(prev => ({
                        ...prev,
                        stats: {
                            ...(prev.stats || {}),
                            visitedCountries: newCountries,
                            visitedContinents: newContinents
                        }
                    }));

                    // DX Hunter: 20 unique countries
                    if (newCountries.length >= 20) unlock('dx_hunter');

                    // Globetrotter: 6 continents
                    if (newContinents.length >= 6) unlock('globetrotter');
                }
                break;
            case 'adventure_complete':
                if (data.inputMethod === 'keyboard') {
                    unlock('adventure_keyboard');
                } else if (data.inputMethod === 'morse') {
                    unlock('adventure_morse');
                }
                break;
            case 'pileup_success':
                pileupCount.current += 1;
                if (pileupCount.current >= 10) unlock('pileup_buster');
                break;
            case 'contest_exchange':
                if (data.isCorrect) {
                    contestStreak.current += 1;
                    if (contestStreak.current >= 5) unlock('contest_king');
                } else {
                    contestStreak.current = 0;
                }
                break;
            case 'head_copy_session':
                unlock('head_copy');
                break;
            case 'zork_location':
                const houseRooms = ['White House', 'Living Room', 'Kitchen', 'Attic'];
                if (houseRooms.includes(data.location)) unlock('zork_explorer');
                break;
            case 'blind_adventure_chapter':
                unlock('blind_adventure');
                break;
            case 'weekly_challenge_complete':
                unlock('weekly_warrior');
                break;
            case 'streak_update':
                if (data.streak >= 3) unlock('streak_starter');
                if (data.streak >= 7) unlock('streak_master');
                if (data.streak >= 30) unlock('streak_legend');
                if (data.streak >= 100) unlock('unstoppable');
                break;
            case 'social_friend_added':
                unlock('social_butterfly');
                break;
            case 'social_message_sent':
                // Track count in ref or userSettings? For simplicity, let's assume the caller tracks count or we use a ref here.
                // Since we don't persist these counts in this hook between sessions easily without userSettings support,
                // we'll use a ref for session-based tracking or assume the event fires on the Nth message.
                // Better approach: The caller (SocialView) should probably track total messages sent in user profile/stats,
                // but for now let's use a ref for "send 5 messages in a session" or similar, OR just unlock if the caller says so.
                // Actually, let's use a ref here for session-based, or rely on the caller passing the total count.
                // Let's use a ref for now.
                // WAIT: The achievement description is "Send 5 messages...". It implies lifetime.
                // Ideally this should be tracked in user stats.
                // For this iteration, let's use a ref to track session progress, or assume the caller passes the total.
                // Let's use a ref `socialMessagesSent`
                socialMessagesSent.current++;
                if (socialMessagesSent.current >= 5) unlock('secret_agent');
                break;
            case 'social_message_decoded':
                socialMessagesDecoded.current++;
                if (socialMessagesDecoded.current >= 5) unlock('signal_intercepted');
                break;
            case 'comprehension_drill_complete':
                if (data.mode === 'wx') {
                    wxDrills.current++;
                    if (wxDrills.current >= 5) unlock('meteorologist');
                } else if (data.mode === 'news') {
                    newsDrills.current++;
                    if (newsDrills.current >= 5) unlock('news_anchor');
                } else if (data.mode === 'stories') {
                    storyDrills.current++;
                    if (storyDrills.current >= 5) unlock('storyteller');
                }
                break;
            case 'head_copy_perfect':
                unlock('mental_giant');
                break;
            case 'health_update':
                // Track low health state
                if (data.health <= 20) {
                    wasLowHealth.current = true;
                }
                if (data.health >= 100 && wasLowHealth.current) {
                    unlock('comeback_kid');
                    wasLowHealth.current = false; // Reset
                }
                break;
            case 'guided_perfect_health':
                unlock('ironclad');
                break;
            case 'trivia_unlocked':
                if (data.count >= 10) unlock('trivia_buff');
                break;
            case 'q_code_identified':
                qCodes.current++;
                if (qCodes.current >= 50) unlock('q_master');
                break;
            case 'abbr_identified':
                abbrs.current++;
                if (abbrs.current >= 50) unlock('short_hand');
                break;
            case 'sent_73':
                unlock('the_73');
                break;
            case 'session_milestone':
                // Century Club achievement (100 sessions)
                if (data.count >= 100) unlock('century_club');
                break;
            case 'constellation_complete':
                // First Light achievement
                unlock('first_light');
                break;
            case 'constellation_perfect':
                // Stargazer's Precision achievement
                unlock('stargazers_precision');
                break;
            case 'constellation_speed':
                // Celestial Speed Record achievement
                unlock('celestial_speed_record');
                break;
            case 'constellation_cartographer':
                // Constellation Cartographer achievement (all 15)
                unlock('constellation_cartographer');
                break;
            case 'signal_lamp_score':
                if (data.score >= 40) unlock('ensign_first_watch');
                if (data.score >= 100) {
                    if (data.noRepeat) unlock('independent_operator');
                    if (data.noTypo) unlock('steel_nerves');
                }
                if (data.score >= 200) unlock('admiral_of_the_fleet');
                // Also check general high score
                if (data.score >= 1000) unlock('score_1000');
                break;
            case 'score_update':
                if (data.score >= 1000) unlock('score_1000');
                break;
            case 'signal_lamp_success':
                if (data.isCorrect) {
                    if (data.lives === 1) unlock('unsinkable');
                    if (data.perfectStreak >= 20) unlock('eagle_eye_lamp');

                    const target = data.target?.toUpperCase() || '';
                    if (data.mode === 'storm' && target.length >= 7) unlock('ghost_in_static');
                    if (target.includes('SOS') || target.includes('MAYDAY') || target.includes('CQD')) {
                        unlock('distress_call_relayed');
                    }
                }
                break;
            case 'signal_lamp_storm_count':
                if (data.count >= 10) unlock('master_of_the_mist');
                break;
            case 'pro_qso_complete':
                // Base QSO achievement
                unlock('first_qso');

                // Persona Specifics
                if (data.persona === 'RAGCHEWER') unlock('pro_qso_ragchewer');
                if (data.persona === 'NEWBIE') unlock('pro_qso_newbie_helper');
                if (data.persona === 'CONTESTER' && data.wpm >= 35) unlock('pro_qso_contester');

                // Hard Mode: Elite Persona + No Assists + Blind Mode
                if (data.persona === 'PRO' && data.isBlind && !data.isExplaining && !data.isFuzzy) {
                    unlock('pro_qso_elite');
                }

                // Omni-Channel: All 4 personas complete (random from data)
                const proStats = userSettings.stats?.proQso || {};
                const completedPersonas = proStats.completedPersonas || [];
                if (!completedPersonas.includes(data.persona)) {
                    const newPersonas = [...completedPersonas, data.persona];
                    setUserSettings(prev => ({
                        ...prev,
                        stats: {
                            ...(prev.stats || {}),
                            proQso: {
                                ...(prev.stats?.proQso || {}),
                                completedPersonas: newPersonas
                            }
                        }
                    }));
                    if (newPersonas.length >= 4) {
                        unlock('pro_qso_persona_master');
                    }
                }
                break;
            case 'grue_death':
                unlock('grue_food');
                break;
            default:
                break;
        }
    }, [unlock]);

    return { checkTimeAchievements, checkSessionAchievements, checkProgress };
};
